<template>
  <el-container class="secure-layout v-self-service-secure-layout">
    <HeaderAlerts
      v-if="showAlerts"
      :show-in="'self-service'"
    />

    <el-header
      v-if="showHeader"
      :bordered="false"
      class="header"
      :color="'fixed-white'"
    >
      <ui-logo></ui-logo>

      <template #actions>
        <el-tag
          v-if="showReadOnlyMode"
          class="read-only-mode-tag mr-8"
        >
          {{ $t('READ-ONLY MODE') }}
        </el-tag>
        <project-chooser />
      </template>

      <template #aside>
        <notification-center class="mr-16" />
        <user-menu />
      </template>
    </el-header>
    <el-container class="secure">
      <el-aside
        v-if="showSidebar"
        class="aside"
        :width="'64px'"
      >
        <self-service-menu :collapsed="true" />
      </el-aside>
      <el-main class="main">
        <div
          v-loading="isLoggingIn || isSwitchingScope"
          class="content"
          :el-loading-background="'fixed-white'"
          :el-loading-size="48"
        >
          <slot v-if="!isSwitchingScope" />
        </div>
      </el-main>
    </el-container>

    <confirm ref="globalConfirm" />
    <global-dialog ref="globalDialog" />

    <template v-if="!iframeView">
      <bottom-notifications />
      <features-config />
    </template>
  </el-container>
</template>

<script>
  import BottomNotifications from '@/common/components/notification-center/BottomNotifications.vue';
  import FeaturesConfig from '@/common/components/FeaturesConfig.vue';
  import SelfServiceMenu from '@/self-service/components/SelfServiceMenu.vue';
  import UiLogo from '@/common/components/UiLogo.vue';
  import ProjectChooser from '@/self-service/components/ProjectChooser.vue';
  import UserMenu from '@/self-service/components/user/Menu.vue';
  import NotificationCenter from '@/common/components/notification-center/NotificationCenter.vue';
  import Confirm from '@/common/shared/Confirm.vue';
  import useAuthStore from '@/common/store/pinia/auth';
  import { mapState } from 'pinia';
  import GlobalDialog from '@/common/shared/GlobalDialog.vue';
  import useAppStore from '@/common/store/pinia/app';
  import HeaderAlerts from '@/common/components/header-alerts/HeaderAlerts.vue';
  import useAppModeStore from '@/common/store/pinia/appMode';
  import { PROJECT_ADMIN_USER_ROLE } from '@/common/constants/domains.constants';

  export default {
    name: 'SelfServiceSecureLayout',
    components: {
      HeaderAlerts,
      Confirm,
      GlobalDialog,
      BottomNotifications,
      NotificationCenter,
      SelfServiceMenu,
      UiLogo,
      ProjectChooser,
      UserMenu,
      FeaturesConfig
    },
    provide() {
      return {
        globalConfirm: () => this.$refs.globalConfirm,
      };
    },
    props: {
      iframeView: Boolean
    },
    mounted() {
      this.$root.$globalConfirm = this.$refs.globalConfirm;
    },
    computed: {
      ...mapState(useAuthStore, ['currentProject', 'isLoggingIn', 'isSwitchingScope', 'scopedRoles']),
      ...mapState(useAppStore, ['isFullscreen']),
      ...mapState(useAppModeStore, ['isVisible', 'isFederated']),
      showHeader() {
        return !this.iframeView && this.isVisible('header');
      },
      showSidebar() {
        return !this.isFullscreen && !this.iframeView && this.isVisible('sidebar');
      },
      showAlerts() {
        return !this.iframeView && !this.isFederated;
      },
      showReadOnlyMode() {
        return !!this.currentProject && !this.scopedRoles.includes(PROJECT_ADMIN_USER_ROLE);
      }
    },
    watch: {
      currentProject: {
        immediate: true,
        handler(newVal) {
          if (newVal) {
            this.$store.dispatch('compute/FETCH_COMPUTE_STATS');
          }
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  .secure-layout {
    flex-direction: column;
    height: 100%;
  }

  .header ::v-deep .el-header__aside {
    $hci-zero-font: 0;

    font-size: $hci-zero-font;
    margin-right: 0;
  }

  .secure {
    overflow: hidden;
    max-height: 100%;
    height: 100%;

    .main {
      flex: 1;
      display: flex;
      overflow: hidden;
      height: 100%;
      border-top: $av-border-sm $av-brand-light;

      .sidebar {
        width: $hci-menu-width;
        display: flex;
        flex-flow: column nowrap;
        height: 100%;

        &-bottom {
          margin-top: auto;
          background: $av-nav-primary;
          height: 64px;
        }

        @media only screen and (max-width: $--md) {
          width: 64px;
          overflow: visible;
        }
      }

      .section {
        flex: 1;
        overflow: hidden;
      }
    }

    @media only screen and (max-width: $--md) {
      .menu {
        overflow: visible;
      }
    }
  }

  .aside {
    overflow: hidden;
  }

  .submenu {
    flex: 0 0 256px;
  }

  .content {
    flex: 1 1 auto;
    min-width: 0;
  }

  ::v-deep .el-header__actions {
    padding-left: 24px;
  }
</style>
